import { Component, Input, Output, EventEmitter, OnInit, AfterViewInit, OnChanges, SimpleChanges, ViewChild, ElementRef } from '@angular/core';
import { ChatAIService, ChatMessage, SupportFAQ, TicketData } from '../../../services/chat-ai.service';

@Component({
    selector: 'app-support-widget',
    templateUrl: './support-widget.component.html',
    styleUrls: ['./support-widget.component.scss']
})
export class SupportWidgetComponent implements OnInit, AfterViewInit, OnChanges {
    @Input() messages: ChatMessage[] = []; // Recibe los mensajes del padre
    @Output() messageAdded = new EventEmitter<ChatMessage>();

    @ViewChild('userInput') userInput!: ElementRef;
    @ViewChild('contentContainer') contentContainer!: ElementRef;

    // Estados
    showFAQs = true;
    showTicketForm = false;
    ticketSubmitted = false;
    userQuery = '';
    isLoading = false;
    private welcomeSent = false;

    // FAQs
    faqs: SupportFAQ[] = [];
    filteredFAQs: SupportFAQ[] = [];

    // Formulario de ticket
    ticketSubject = '';
    ticketEmail = '';
    ticketDescription = '';
    submittedTicketId = '';

    constructor(private chatAIService: ChatAIService) { }

    ngOnInit(): void {
        // Mostrar mensaje de bienvenida solo una vez
        if (!this.welcomeSent && this.messages.length === 0) {
            const welcomeMsg: ChatMessage = {
                id: this.generateId(),
                role: 'assistant',
                content: '¡Hola! Soy tu asistente de soporte. ¿Cómo puedo ayudarte? Busca en nuestras preguntas frecuentes.',
                timestamp: Date.now(),
                type: 'text'
            };
            this.messageAdded.emit(welcomeMsg);
            this.welcomeSent = true;
        }

        // Cargar FAQs
        this.loadFAQs();
    }

    ngAfterViewInit(): void {
        // Hacer scroll inicial al cargar el componente
        setTimeout(() => this.scrollToBottom(), 200);
    }

    /**
     * Detecta cambios en los inputs (especialmente en messages)
     */
    ngOnChanges(changes: SimpleChanges): void {
        // Solo hacer scroll si cambiaron los mensajes
        if (changes['messages'] && changes['messages'].currentValue) {
            // Esperar a que Angular renderice los cambios antes de hacer scroll
            setTimeout(() => this.scrollToBottom(), 200);
        }
    }

    /**
     * Carga las FAQs desde el servicio
     */
    private loadFAQs(): void {
        this.faqs = this.chatAIService.getAllFAQs();
        this.filteredFAQs = this.faqs;
    }

    /**
     * Busca una respuesta en las FAQs
     */
    searchSupport(): void {
        if (!this.userQuery.trim()) {
            this.filteredFAQs = this.faqs;
            return;
        }

        // Agregar mensaje del usuario
        const userMessage: ChatMessage = {
            id: this.generateId(),
            role: 'user',
            content: this.userQuery,
            timestamp: Date.now(),
            type: 'text'
        };

        this.messageAdded.emit(userMessage);
        this.isLoading = true;

        // Llamar al servicio para buscar respuestas
        this.chatAIService.getSupportResponse(this.userQuery).subscribe(
            (faqs: SupportFAQ[]) => {
                this.filteredFAQs = faqs;
                this.isLoading = false;

                if (faqs.length === 0) {
                    const noResultsMsg: ChatMessage = {
                        id: this.generateId(),
                        role: 'assistant',
                        content: 'No encontré respuestas que coincidan con tu búsqueda. Intenta con otros términos o vuelve a la lista de preguntas frecuentes.',
                        timestamp: Date.now(),
                        type: 'text'
                    };
                    this.messageAdded.emit(noResultsMsg);
                    // Limpiar filtros para mostrar todas las FAQs nuevamente
                    this.filteredFAQs = this.faqs;
                } else {
                    const resultMsg: ChatMessage = {
                        id: this.generateId(),
                        role: 'assistant',
                        content: `Encontré ${faqs.length} respuesta(s) que podría(n) ayudarte:`,
                        timestamp: Date.now(),
                        type: 'faq'
                    };
                    this.messageAdded.emit(resultMsg);
                }

                this.userQuery = '';

                // Hacer scroll al final para ver los resultados
                setTimeout(() => this.scrollToBottom(), 300);
            },
            (error) => {
                console.error('Error searching support:', error);
                this.isLoading = false;

                const errorMessage: ChatMessage = {
                    id: this.generateId(),
                    role: 'assistant',
                    content: 'Hubo un error al buscar. Por favor intenta de nuevo.',
                    timestamp: Date.now(),
                    type: 'text'
                };

                this.messageAdded.emit(errorMessage);
            }
        );
    }

    /**
     * Maneja el clic en una FAQ
     */
    clickFAQ(faq: SupportFAQ): void {
        const message: ChatMessage = {
            id: this.generateId(),
            role: 'assistant',
            content: `**${faq.question}**\n\n${faq.answer}`,
            timestamp: Date.now(),
            type: 'faq'
        };

        this.messageAdded.emit(message);
        this.showFAQs = false;

        // Hacer scroll al final para ver la respuesta
        setTimeout(() => this.scrollToBottom(), 300);
    }

    /**
     * Abre el formulario de ticket
     */
    openTicketForm(): void {
        this.showFAQs = false;
        this.showTicketForm = true;
    }

    /**
     * Cancela el formulario de ticket
     */
    cancelTicket(): void {
        this.showTicketForm = false;
        this.showFAQs = true;
        this.ticketSubject = '';
        this.ticketEmail = '';
        this.ticketDescription = '';
    }

    /**
     * Envía el formulario de ticket
     */
    submitTicket(): void {
        if (!this.ticketSubject.trim() || !this.ticketDescription.trim()) {
            alert('Por favor completa todos los campos requeridos.');
            return;
        }

        this.isLoading = true;

        // Preparar datos del ticket
        const ticketData: TicketData = {
            subject: this.ticketSubject,
            description: this.ticketDescription,
            userEmail: this.ticketEmail,
            conversation: []  // Los mensajes se enviarán desde el componente padre
        };

        // Agregar mensaje del usuario
        const userMessage: ChatMessage = {
            id: this.generateId(),
            role: 'user',
            content: `Asunto: ${this.ticketSubject}\n\n${this.ticketDescription}`,
            timestamp: Date.now(),
            type: 'text'
        };

        this.messageAdded.emit(userMessage);

        // Enviar ticket al backend
        this.chatAIService.openTicket(ticketData).subscribe(
            (response) => {
                this.isLoading = false;

                const confirmMessage: ChatMessage = {
                    id: this.generateId(),
                    role: 'assistant',
                    content: `✓ ${response.message}\n\nID del ticket: ${response.ticketId}`,
                    timestamp: Date.now(),
                    type: 'text'
                };

                this.messageAdded.emit(confirmMessage);

                // Guardar información del ticket y cambiar estado
                this.submittedTicketId = response.ticketId;
                this.showTicketForm = false;
                this.showFAQs = false;
                this.ticketSubmitted = true;

                // Limpiar formulario
                this.ticketSubject = '';
                this.ticketEmail = '';
                this.ticketDescription = '';

                // Hacer scroll al final para ver la confirmación
                setTimeout(() => this.scrollToBottom(), 300);
            },
            (error) => {
                console.error('Error creating ticket:', error);
                this.isLoading = false;

                const errorMessage: ChatMessage = {
                    id: this.generateId(),
                    role: 'assistant',
                    content: 'Error al crear el ticket. Por favor intenta de nuevo.',
                    timestamp: Date.now(),
                    type: 'text'
                };

                this.messageAdded.emit(errorMessage);
            }
        );
    }

    /**
     * Maneja el clic en Enter en el input
     */
    onKeyPress(event: KeyboardEvent): void {
        if (event.key === 'Enter' && !event.shiftKey) {
            event.preventDefault();
            this.searchSupport();
        }
    }

    /**
     * Vuelve a mostrar las FAQs después de enviar un ticket o si no le ayudó la respuesta
     */
    backToFAQs(): void {
        const backMsg: ChatMessage = {
            id: this.generateId(),
            role: 'assistant',
            content: 'Entiendo, volvamos a la lista de preguntas frecuentes. ¿Hay algo más en lo que pueda ayudarte?',
            timestamp: Date.now(),
            type: 'text'
        };

        this.messageAdded.emit(backMsg);
        this.ticketSubmitted = false;
        this.showFAQs = true;
        this.submittedTicketId = '';
        this.userQuery = '';

        // Hacer scroll al final
        setTimeout(() => this.scrollToBottom(), 300);
    }

    /**
     * Marca el problema como resuelto
     */
    markAsResolved(): void {
        const resolvedMsg: ChatMessage = {
            id: this.generateId(),
            role: 'assistant',
            content: '¡Excelente! Me alegra haber podido ayudarte. Si necesitas algo más, no dudes en preguntar.',
            timestamp: Date.now(),
            type: 'text'
        };

        this.messageAdded.emit(resolvedMsg);
        this.showFAQs = true;
        this.userQuery = '';

        // Hacer scroll al final
        setTimeout(() => this.scrollToBottom(), 300);
    }

    /**
     * Hace scroll al final del contenedor de contenido
     */
    private scrollToBottom(): void {
        try {
            if (this.contentContainer && this.contentContainer.nativeElement) {
                const element = this.contentContainer.nativeElement;
                element.scrollTop = element.scrollHeight;
            }
        } catch (err) {
            // Ignorar errores de scroll
        }
    }

    /**
     * Genera un ID único
     */
    private generateId(): string {
        return `msg-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
    }
}
